/**
 * What Brand Setup will accept as a thing to read a brand from — checked in
 * the browser, before any bytes leave it.
 *
 * The server checks the same rules (`src/server/brandStudyRoutes.ts`: the same
 * extension allow-list, images at 8 MiB, video at `QL_MAX_UPLOAD_BYTES`), so
 * this is not the security boundary — it is the difference between "that isn't
 * a video" said instantly and said after a two-minute upload.
 *
 * Pure, so the rules are unit-tested rather than clicked.
 */

export const MAX_ASSET_IMAGE_BYTES = 8 * 1024 * 1024;
/** The server's `QL_MAX_UPLOAD_BYTES` default. */
export const MAX_ASSET_VIDEO_BYTES = 2 * 1024 * 1024 * 1024;

const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm", "video/x-m4v"];
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];

/** Fallback when the browser reports no (or an unhelpful) MIME type — common
 *  for .mov on Windows and for anything dragged out of an archive. */
const EXT_KIND: Record<string, "video" | "image"> = {
  mp4: "video",
  mov: "video",
  m4v: "video",
  webm: "video",
  png: "image",
  jpg: "image",
  jpeg: "image",
  webp: "image",
};

/** The fields of a `File` this needs — so a test doesn't have to build one. */
export interface AssetCandidate {
  name: string;
  type: string;
  size: number;
}

export type AssetVerdict = { kind: "video" | "image" } | { error: string };

/** `{ kind }` when we can read this file, `{ error }` — a SENTENCE, one the
 *  user can act on — when we cannot. Never throws. */
export function classifyAsset(file: AssetCandidate): AssetVerdict {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  const kind: "video" | "image" | undefined = IMAGE_TYPES.includes(file.type)
    ? "image"
    : VIDEO_TYPES.includes(file.type)
      ? "video"
      : EXT_KIND[ext];
  if (!kind) {
    return { error: `We can't read ${file.name} — send an MP4, MOV, WebM, PNG, JPG or WebP.` };
  }
  const cap = kind === "image" ? MAX_ASSET_IMAGE_BYTES : MAX_ASSET_VIDEO_BYTES;
  if (file.size > cap) {
    return {
      error:
        kind === "image"
          ? `${file.name} is over 8 MB — a screenshot is plenty.`
          : `${file.name} is over 2 GB — that's larger than we can take.`,
    };
  }
  return { kind };
}
